import { batch, createSignal } from "solid-js"
import type { QueuedServerEvent } from "./global-sdk"

/** A connection's replay is committed only after the server's connected barrier. */
export function createEventStreamReplay(input: {
  emit: (event: QueuedServerEvent) => void
  enqueue: (event: QueuedServerEvent) => void
  drain: () => void
}) {
  const [catchingUp, setCatchingUp] = createSignal(true)
  // Workspaces whose events the committed stream delivers without a gap.
  let coverage: ReadonlySet<string> = new Set()
  let cursor: string | undefined
  let generation = 0
  let resumeToken: string | undefined

  return {
    catchingUp,
    /** The last event this page committed: a request sent now is handled after it. */
    cursor: () => cursor,
    suspend: () => setCatchingUp(true),
    resume(token: string) {
      setCatchingUp(true)
      batch(input.drain)
      resumeToken = token
    },
    begin(directories: readonly string[] = []) {
      setCatchingUp(true)
      resumeToken = undefined
      batch(input.drain)
      const covered = coverage
      const added = directories.filter((directory) => !covered.has(directory))
      coverage = new Set(directories.filter((directory) => covered.has(directory)))
      const current = ++generation
      const from = cursor
      let replaying = true
      let resync = false
      let pendingCursor = cursor
      const pending: QueuedServerEvent[] = []
      return {
        cursor: from,
        receive(event: QueuedServerEvent, id?: string) {
          if (current !== generation) return
          const type = event.payload.type as string
          if (type === "server.resync-required") {
            resync = true
            cursor = undefined
            pendingCursor = undefined
            setCatchingUp(true)
          } else if (id && (!type.startsWith("server.") || type === "server.barrier" || type === "server.connected")) pendingCursor = id

          if (!replaying && resumeToken) {
            const token = (event.payload.properties as { token?: string }).token
            if (type !== "server.barrier" || token !== resumeToken) {
              pending.push(event)
              return
            }
            batch(() => {
              for (const queued of pending) input.emit(queued)
            })
            pending.length = 0
            resumeToken = undefined
            cursor = pendingCursor
            setCatchingUp(false)
            return
          }
          if (replaying && type !== "server.connected") {
            pending.push(event)
            return
          }
          if (replaying) {
            replaying = false
            batch(() => {
              for (const queued of pending) input.emit(queued)
              // Resumed: nothing was missed since the committed cursor, including
              // cross-workspace summary events. Replayed also needs no new coverage.
              const resumed = !!from && !resync
              input.emit({
                ...event,
                payload: { ...event.payload, properties: { replayed: resumed && added.length === 0, resumed, resyncDirectories: resumed ? added : [...directories] } } as typeof event.payload,
              })
            })
            pending.length = 0
            cursor = pendingCursor
            coverage = new Set(directories)
            // Let effects observe the committed state with presentation disabled.
            setCatchingUp(false)
            return
          }
          input.enqueue(event)
          cursor = pendingCursor
        },
        close() {
          if (current !== generation) return
          generation++
          pending.length = 0
          setCatchingUp(true)
        },
      }
    },
  }
}
